import React, { useContext, useState, useEffect, useRef } from 'react';
import { LanguageContext } from '../../../../context/LanguageContext';
import { FaTimes, FaEdit, FaCheck } from 'react-icons/fa';
import { formatCurrency } from '../../../../utils/formatters';

// Separate component for each item row to properly use hooks
const ItemRow = ({ item, language, handleUpdateQuantity, handleUpdatePrice, handleRemoveItem }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editPrice, setEditPrice] = useState(item.price);
    
    return (
        <div className={`flex items-center justify-between bg-gray-50 p-2 rounded ${language === 'ar' ? 'flex-row-reverse' : 'flex-row'}`} dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <div className={`flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : 'flex-row'}`}>
                <button
                    type="button"
                    onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                    className="px-2 py-1 text-sm bg-gray-200 rounded"
                >
                    -
                </button>
                <span>{item.quantity}</span>
                <button
                    type="button"
                    onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                    className="px-2 py-1 text-sm bg-gray-200 rounded"
                >
                    +
                </button>
                <span className={language === 'ar' ? 'text-right' : 'text-left'}>{language === 'ar' ? item.name_ar : item.name}</span>
            </div>
            <div className={`flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : 'flex-row'}`}>
                {isEditing ? (
                    <div className={`flex items-center gap-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                        <input
                            type="number"
                            value={editPrice}
                            onChange={(e) => setEditPrice(parseFloat(e.target.value) || 0)}
                            className="w-20 px-2 py-1 text-sm border rounded"
                            step="0.01"
                            min="0"
                        />
                        <button
                            type="button"
                            onClick={() => {
                                handleUpdatePrice(item.id, editPrice);
                                setIsEditing(false);
                            }}
                            className="text-green-600 hover:text-green-700"
                        >
                            <FaCheck className="w-4 h-4" />
                        </button>
                    </div>
                ) : (
                    <div className={`flex items-center gap-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                        <span>{formatCurrency(item.price * item.quantity)}</span>
                        <button
                            type="button"
                            onClick={() => {
                                setEditPrice(item.price);
                                setIsEditing(true);
                            }}
                            className="text-blue-600 hover:text-blue-700"
                        >
                            <FaEdit className="w-4 h-4" />
                        </button>
                    </div>
                )}
                <button
                    type="button"
                    onClick={() => handleRemoveItem(item.id)}
                    className="text-red-600 hover:text-red-700"
                >
                    <FaTimes className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

const ItemsList = ({ 
    items, 
    formItems, 
    selectedItem, 
    setSelectedItem, 
    handleAddItem, 
    handleRemoveItem, 
    handleUpdateQuantity,
    handleUpdatePrice
}) => {
    const { language } = useContext(LanguageContext);
    const [searchQuery, setSearchQuery] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    
    // Filter items based on search query
    const filteredItems = items.filter(item => {
        const searchLower = searchQuery.toLowerCase();
        const nameMatch = (language === 'ar' ? item.name_ar : item.name).toLowerCase().includes(searchLower);
        return nameMatch;
    });
    
    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
                !event.target.closest('.items-dropdown-menu')) {
                setIsDropdownOpen(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    
    // Close dropdown when component unmounts
    useEffect(() => {
        return () => {
            setIsDropdownOpen(false);
        };
    }, []);
    
    const handleItemSelect = (itemId) => {
        setSelectedItem(itemId);
        // Find the selected item to display its name in the search field
        const selectedItemObj = items.find(item => item.id === itemId);
        if (selectedItemObj) {
            setSearchQuery(language === 'ar' ? selectedItemObj.name_ar : selectedItemObj.name);
        }
        setIsDropdownOpen(false);
    };

    return (
        <div>
            <div className="space-y-2">
                {formItems.map((item) => (
                    <ItemRow 
                        key={item.id}
                        item={item}
                        language={language}
                        handleUpdateQuantity={handleUpdateQuantity}
                        handleUpdatePrice={handleUpdatePrice}
                        handleRemoveItem={handleRemoveItem}
                    />
                ))}
            </div>
            <div className="mt-2 relative">
                <div className={`flex gap-2 ${language === 'ar' ? 'flex-row-reverse' : 'flex-row'}`}>
                    <div className="relative w-full" ref={dropdownRef}>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setIsDropdownOpen(true);
                            }}
                            onFocus={() => setIsDropdownOpen(true)}
                            placeholder={language === 'ar' ? 'ابحث عن منتج...' : 'Search for an item...'}
                            className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm ${language === 'ar' ? 'text-right pr-3' : 'text-left'}`}
                            dir={language === 'ar' ? 'rtl' : 'ltr'}
                        />
                        {isDropdownOpen && filteredItems.length > 0 && (
                            <div 
                                className="absolute z-50 bg-white shadow-lg rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm items-dropdown-menu" 
                                style={{
                                    width: '100%',
                                    maxHeight: '300px',
                                    top: '100%',
                                    marginTop: '2px',
                                    ...(language === 'ar' 
                                        ? { right: 0 }
                                        : { left: 0 })
                                }}
                                dir={language === 'ar' ? 'rtl' : 'ltr'}
                            >
                                {filteredItems.map((item) => (
                                    <div
                                        key={item.id}
                                        onClick={() => handleItemSelect(item.id)}
                                        className="cursor-pointer hover:bg-gray-100 px-4 py-2 flex justify-between items-center"
                                        dir={language === 'ar' ? 'rtl' : 'ltr'}
                                    >
                                        <span className={language === 'ar' ? 'text-right' : 'text-left'}>{language === 'ar' ? item.name_ar : item.name}</span>
                                        <span className="text-gray-500">{formatCurrency(item.price)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            handleAddItem();
                            setSearchQuery('');
                            setSelectedItem('');
                        }}
                        disabled={!selectedItem}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {language === 'ar' ? 'إضافة' : 'Add'}
                    </button>
                </div>
                {isDropdownOpen && filteredItems.length === 0 && searchQuery && (
                    <div className={`mt-1 text-sm text-gray-500 ${language === 'ar' ? 'text-right' : 'text-left'}`} dir={language === 'ar' ? 'rtl' : 'ltr'}>
                        {language === 'ar' ? 'لا توجد منتجات مطابقة' : 'No matching items found'}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ItemsList;
